import { useState, useCallback } from 'react';
import { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Project } from './workbook-types';
import { updateWorkbookDisplayOrder } from './use-workbook-order-mutations';
import { useQueryClient } from '@tanstack/react-query';

interface UseWorkbookDnDProps {
  items: Project[];
  onItemsReorder: (reorderedItems: Project[]) => void;
}

/**
 * Calculate position for inserting a card
 * Exact logic: divide by 2 for first, add 1000 for last, precise midpoint for between
 */
function calculatePosition(prevOrder: number | undefined, nextOrder: number | undefined): number {
  // Moving to first position - divide the card you're replacing by 2
  if (prevOrder === undefined && nextOrder !== undefined) {
    return parseFloat((nextOrder / 2).toFixed(10));
  }
  
  // Moving to last position - add 1000 to the end card
  if (prevOrder !== undefined && nextOrder === undefined) {
    return prevOrder + 1000;
  }
  
  // Moving between cards - EXACTLY between, up to 10 decimal places
  if (prevOrder !== undefined && nextOrder !== undefined) {
    return parseFloat(((prevOrder + nextOrder) / 2).toFixed(10));
  }
  
  // Empty list fallback
  return 1000;
}

export const useWorkbookDnD = ({
  items,
  onItemsReorder,
}: UseWorkbookDnDProps) => {
  const [activeItem, setActiveItem] = useState<Project | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const queryClient = useQueryClient();

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const item = items.find(item => item.id === active.id);
    
    if (item) {
      setActiveItem(item);
      console.log('[WorkbookDnD] Drag started for item:', item.title);
    }
  }, [items]);

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    setActiveItem(null);

    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      console.log('[WorkbookDnD] Drag ended without valid drop');
      return;
    }

    const oldIndex = items.findIndex(item => item.id === active.id);
    const newIndex = items.findIndex(item => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      console.log('[WorkbookDnD] Could not find item indices');
      return;
    }

    console.log(`[WorkbookDnD] Moving item from index ${oldIndex} to ${newIndex}`);

    // 1. Immediate UI update (optimistic) - like File Storage
    const reorderedItems = arrayMove(items, oldIndex, newIndex);
    const movedItem = items[oldIndex];
    
    // Calculate new position between neighbors
    const prevItem = newIndex > 0 ? reorderedItems[newIndex - 1] : undefined;
    const nextItem = newIndex < reorderedItems.length - 1 ? reorderedItems[newIndex + 1] : undefined;
    
    const newPosition = calculatePosition(
      prevItem?.displayOrder, 
      nextItem?.displayOrder
    );

    // Update local state with new position immediately
    const updatedItems = reorderedItems.map(item => 
      item.id === movedItem.id 
        ? { ...item, displayOrder: newPosition }
        : item
    );
    
    onItemsReorder(updatedItems);

    console.log(`[WorkbookDnD] Calculated new position: ${newPosition} (between ${prevItem?.displayOrder || 'start'} and ${nextItem?.displayOrder || 'end'})`);

    // 2. Update database silently in background (like File Storage)
    try {
      setIsUpdating(true);
      await updateWorkbookDisplayOrder(movedItem.id, newPosition);
      console.log('[WorkbookDnD] Database update completed successfully');
      
      // 🎯 INVALIDATE DROPDOWN CACHE: Refresh Projects dropdown instantly
      queryClient.invalidateQueries({ 
        queryKey: ['workbook-projects'],
        exact: false 
      });
      console.log('[WorkbookDnD] Projects dropdown cache invalidated for instant update');
      
    } catch (error) {
      console.error('[WorkbookDnD] Database update failed:', error);
      
      // On error, revert to original order (error recovery)
      onItemsReorder(items);
      
      // You could show a toast notification here if desired
      // toast.error('Failed to save new order. Please try again.');
    } finally {
      setIsUpdating(false);
    }
    
  }, [items, onItemsReorder]);

  return {
    activeItem,
    handleDragStart,
    handleDragEnd,
    isUpdating,
  };
}; 